import { computed, inject, ref, shallowRef } from "vue";
import { TestId } from "#/test-ids";

const STATUS = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
});

// The select each option list fills, in the order the dialog shows them:
//   - name:   the parameter the form posts the choice as
//   - label:  i18n key of the select's label
//   - empty:  i18n key of the problem shown when the list is empty
const FIELDS = Object.freeze([
  {
    list: "visitTypes",
    name: "visit_type_id",
    label: "visit_type_label",
    empty: "no_visit_types",
    testId: TestId.epicWidgetLauncher.visitType,
  },
  {
    list: "departments",
    name: "department_id",
    label: "department_label",
    empty: "no_departments",
    testId: TestId.epicWidgetLauncher.department,
  },
  {
    list: "providers",
    name: "physician_id",
    label: "provider_label",
    empty: "no_providers",
    testId: TestId.epicWidgetLauncher.provider,
  },
]);

export const EPIC_WIDGET_LAUNCHER_KEY = Symbol("epic-widget-launcher");

export function useEpicWidgetLauncherStore() {
  const store = inject(EPIC_WIDGET_LAUNCHER_KEY);
  if (!store) {
    throw new Error("Epic widget launcher store must be provided by the app");
  }
  return store;
}

/**
 * Creates the Epic widget launcher's state: whether the dialog is open, and the options it offers.
 *
 * @param {Object} options
 * @param {Object} options.api - An Epic widget launcher API from `createEpicWidgetLauncherApi`.
 * @param {String} [options.physicianId] - Id of the Physician the dialog opens MyChart for, if any.
 */
export function createEpicWidgetLauncherStore({ api, physicianId }) {
  const open = ref(false);
  const status = ref(STATUS.IDLE);
  const lists = shallowRef({});

  const loading = computed(() => status.value === STATUS.LOADING);
  const failed = computed(() => status.value === STATUS.ERROR);

  const fields = computed(() =>
    FIELDS.filter((field) => Array.isArray(lists.value[field.list])).map((field) => {
      const items = lists.value[field.list];

      return { ...field, items, defaultValue: items[0]?.value ?? "", isEmpty: items.length === 0 };
    }),
  );

  const emptyField = computed(() => fields.value.find((field) => field.isEmpty) ?? null);
  const canSubmit = computed(() => status.value === STATUS.READY && !emptyField.value);

  // Loads the options the first time the dialog opens. A failed load runs again on the next open.
  async function load() {
    if (status.value === STATUS.LOADING || status.value === STATUS.READY) {
      return;
    }

    status.value = STATUS.LOADING;

    try {
      lists.value = await api.options({ physicianId });
      status.value = STATUS.READY;
    } catch {
      status.value = STATUS.ERROR;
    }
  }

  function setOpen(value) {
    open.value = value;

    if (value) {
      load();
    }
  }

  function close() {
    setOpen(false);
  }

  return { open, loading, failed, fields, emptyField, canSubmit, setOpen, close };
}
